// Parser del stream SSE del agente (formato Anthropic), bloque por bloque.
//
// Patrón copiado de `useV2Chat` (que a su vez lo copió del Director de
// Estudio), pero escrito acá y AISLADO de React para poder testearlo: se le
// dan chunks de texto y se le pregunta qué salió.
//
// Reglas del formato:
//   • Los eventos vienen separados por línea en blanco (`\n\n`).
//   • Dentro de cada evento, las líneas `data:` se concatenan con `\n`.
//   • Las líneas que empiezan con `:` son comentarios (el backend manda
//     `: ping` cada 15s con `keepalive:true`) y se ignoran.
//   • Un chunk puede partir un evento por la mitad: hay que bufferear.

/** Bloque de contenido ya cerrado de la respuesta del agente. */
export type BloqueRespuesta =
  | { tipo: 'texto'; texto: string }
  | {
      tipo: 'herramienta';
      id: string;
      nombre: string;
      entrada: Record<string, unknown>;
    };

export interface EventosLector {
  /** Cada fragmento de texto que llega (para pintar el streaming). */
  onTexto?: (fragmento: string) => void;
  /** Etiqueta de la herramienta server-side en curso, o null cuando termina. */
  onActividad?: (etiqueta: string | null) => void;
}

/** Etiqueta amigable para el indicador mientras corre una tool server-side. */
export const etiquetaActividad = (tipoBloque: string, nombre: string): string => {
  if (tipoBloque === 'server_tool_use') return 'Buscando en la web…';
  const n = (nombre || '').toLowerCase();
  if (n.includes('instagram')) return 'Analizando Instagram…';
  if (n.includes('ads-library') || n.includes('facebook-ad'))
    return 'Revisando anuncios de Facebook…';
  if (n.includes('facebook')) return 'Analizando Facebook…';
  if (n.includes('places') || n.includes('google'))
    return 'Buscando lugares en Google…';
  if (n.includes('commerce')) return 'Buscando productos…';
  return 'Buscando datos…';
};

interface BloqueEnCurso {
  tipo: 'texto' | 'herramienta';
  texto: string;
  id: string;
  nombre: string;
  json: string;
  entrada: Record<string, unknown>;
}

export class LectorStreamChatAtlas {
  /** Texto completo acumulado del turno. */
  texto = '';
  /** `stop_reason` del `message_delta` (null si el stream no lo trajo). */
  stopReason: string | null = null;

  private eventos: EventosLector;
  private buffer = '';
  private bloques = new Map<number, BloqueEnCurso>();
  private actividad: string | null = null;

  constructor(eventos: EventosLector = {}) {
    this.eventos = eventos;
  }

  /** Alimenta el lector con un chunk (puede cortar un evento por la mitad). */
  alimentar(chunk: string): void {
    this.buffer += chunk.replace(/\r\n/g, '\n');
    let corte = this.buffer.indexOf('\n\n');
    while (corte !== -1) {
      const bloque = this.buffer.slice(0, corte);
      this.buffer = this.buffer.slice(corte + 2);
      if (bloque.trim()) this.procesarEvento(bloque);
      corte = this.buffer.indexOf('\n\n');
    }
  }

  /** Procesa lo que quedó en el buffer cuando el stream cierra. */
  finalizar(): void {
    if (this.buffer.trim()) this.procesarEvento(this.buffer);
    this.buffer = '';
    // Un stream cortado puede dejar un tool_use sin su `content_block_stop`:
    // cerramos lo que haya para no perder la llamada.
    for (const b of this.bloques.values()) {
      if (b.tipo === 'herramienta' && b.json) this.cerrarHerramienta(b);
    }
    if (this.actividad !== null) this.setActividad(null);
  }

  /** Bloques cerrados, en el orden en que los mandó el modelo. */
  bloquesOrdenados(): BloqueRespuesta[] {
    return [...this.bloques.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([, b]): BloqueRespuesta | null =>
        b.tipo === 'texto'
          ? b.texto
            ? { tipo: 'texto', texto: b.texto }
            : null
          : { tipo: 'herramienta', id: b.id, nombre: b.nombre, entrada: b.entrada },
      )
      .filter((b): b is BloqueRespuesta => b !== null);
  }

  /** Solo las llamadas a herramientas, en orden. */
  herramientasUsadas(): Array<Extract<BloqueRespuesta, { tipo: 'herramienta' }>> {
    return this.bloquesOrdenados().filter(
      (b): b is Extract<BloqueRespuesta, { tipo: 'herramienta' }> =>
        b.tipo === 'herramienta',
    );
  }

  // ── interno ──────────────────────────────────────────────────────────

  private setActividad(v: string | null): void {
    if (this.actividad === v) return;
    this.actividad = v;
    this.eventos.onActividad?.(v);
  }

  private nuevoBloque(tipo: 'texto' | 'herramienta'): BloqueEnCurso {
    return { tipo, texto: '', id: '', nombre: '', json: '', entrada: {} };
  }

  private cerrarHerramienta(b: BloqueEnCurso): void {
    try {
      const parseado = b.json ? JSON.parse(b.json) : {};
      b.entrada =
        parseado && typeof parseado === 'object' && !Array.isArray(parseado)
          ? (parseado as Record<string, unknown>)
          : {};
    } catch {
      b.entrada = {};
    }
    b.json = '';
  }

  private procesarEvento(bloqueSse: string): void {
    const datos: string[] = [];
    for (const linea of bloqueSse.split('\n')) {
      if (linea.startsWith(':')) continue; // comentario / ping
      if (linea.startsWith('data:')) datos.push(linea.slice(5).trimStart());
    }
    if (datos.length === 0) return;
    const crudo = datos.join('\n');
    if (crudo === '[DONE]') return;

    let evt: any;
    try {
      evt = JSON.parse(crudo);
    } catch {
      return; // evento malformado: se ignora (igual que V2)
    }
    if (!evt || typeof evt !== 'object') return;

    if (evt.type === 'content_block_start') {
      const idx = evt.index as number;
      const cb = evt.content_block;
      if (cb?.type === 'tool_use') {
        const b = this.nuevoBloque('herramienta');
        b.id = String(cb.id ?? '');
        b.nombre = String(cb.name ?? '');
        this.bloques.set(idx, b);
      } else if (cb?.type === 'text') {
        this.bloques.set(idx, this.nuevoBloque('texto'));
      } else if (cb?.type === 'server_tool_use' || cb?.type === 'mcp_tool_use') {
        this.setActividad(etiquetaActividad(cb.type, cb.name));
      } else if (
        cb?.type === 'web_search_tool_result' ||
        cb?.type === 'mcp_tool_result'
      ) {
        this.setActividad(null);
      }
      return;
    }

    if (evt.type === 'content_block_delta') {
      const idx = evt.index as number;
      let b = this.bloques.get(idx);
      if (evt.delta?.type === 'text_delta') {
        const pedazo = String(evt.delta.text ?? '');
        if (!pedazo) return;
        // Si el modelo ya escribe, no hay tool server-side en curso.
        this.setActividad(null);
        if (!b) {
          b = this.nuevoBloque('texto');
          this.bloques.set(idx, b);
        }
        if (b.tipo === 'texto') b.texto += pedazo;
        this.texto += pedazo;
        this.eventos.onTexto?.(pedazo);
      } else if (evt.delta?.type === 'input_json_delta' && b?.tipo === 'herramienta') {
        b.json += String(evt.delta.partial_json ?? '');
      }
      return;
    }

    if (evt.type === 'content_block_stop') {
      const b = this.bloques.get(evt.index as number);
      if (b?.tipo === 'herramienta') this.cerrarHerramienta(b);
      return;
    }

    if (evt.type === 'message_delta' && evt.delta?.stop_reason) {
      this.stopReason = String(evt.delta.stop_reason);
    }
  }
}
